import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import {
  ArbitrationActivityItem,
  ArbitrationCaseItem,
  ArbitrationCasePayload,
  ArbitrationDistrictItem,
  ArbitrationDashboardSummary,
  ArbitrationOfficerItem,
  ArbitrationService,
} from "../services/arbitration.service";
import { ProjectService } from "../services/project.service";

interface SelectOption {
  id: number | string;
  name: string;
}

@Component({
  selector: "app-arbitration-cases",
  templateUrl: "./arbitration-cases.component.html",
  styleUrls: ["./arbitration-cases.component.css"],
})
export class ArbitrationCasesComponent implements OnInit, OnDestroy {
  @ViewChild("caseDialogTemplate") caseDialogTemplate: TemplateRef<any>;
  @ViewChild("activityDialogTemplate") activityDialogTemplate: TemplateRef<any>;

  cases: ArbitrationCaseItem[] = [];
  summary: ArbitrationDashboardSummary = {};
  projects: SelectOption[] = [];
  districts: SelectOption[] = [];
  officers: SelectOption[] = [];
  selectedCaseActivities: ArbitrationActivityItem[] = [];

  filters = {
    project: null,
    district: null,
    officer: null,
    search: "",
  };

  caseForm: FormGroup;
  activityForm = {
    activity_type: "comment",
    message: "",
  };

  loading = false;
  saving = false;
  deletingId: number | null = null;
  lookupsLoading = false;
  panelOpen = false;
  activityPanelOpen = false;
  editingCase: ArbitrationCaseItem | null = null;
  selectedCase: ArbitrationCaseItem | null = null;
  errorMessage = "";
  private dialogRef: MatDialogRef<any> | null = null;

  readonly activityTypeOptions = ["comment", "update"];

  constructor(
    private fb: FormBuilder,
    private arbitrationService: ArbitrationService,
    private projectService: ProjectService,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {
    this.caseForm = this.fb.group({
      id: [null],
      project: [null, Validators.required],
      district: [null, Validators.required],
      officer: [null, Validators.required],
      total_cases: [null, Validators.required],
      cases_on_hearing: [null, Validators.required],
      disposed_cases: [null, Validators.required],
      comments: [""],
      register_date: [this.getToday(), Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadLookups();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.dialogRef?.close();
  }

  loadData(): void {
    this.loading = true;
    this.errorMessage = "";

    this.arbitrationService.listCases(this.getFiltersQuery()).subscribe(
      (res) => {
        this.cases = Array.isArray(res) ? res : [];
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.errorMessage = "Unable to load arbitration cases.";
      }
    );

    this.arbitrationService.getDashboard(this.getFiltersQuery()).subscribe(
      (res) => {
        this.summary = res || {};
      },
      () => {
        this.summary = {};
      }
    );
  }

  loadLookups(): void {
    this.lookupsLoading = true;

    this.projectService.allProjectList().subscribe(
      (res) => {
        const items = Array.isArray(res) ? res : res?.results || [];
        console.log('Project list', items);
        
        this.projects = (items || []).map((item: any) => ({
          id: item?.id || item?.project_id,
          name: item?.project_name || item?.name || "Unnamed Project",
        }));
      },
      () => {
        this.projects = [];
      }
    );

    this.arbitrationService.listDistricts().subscribe(
      (res) => {
        const items = Array.isArray(res) ? res : [];
        this.districts = items.map((item: ArbitrationDistrictItem) => ({
          id: item.id,
          name: item.name || "Unnamed District",
        }));
      },
      () => {
        this.districts = [];
      }
    );

    this.arbitrationService.listOfficers().subscribe(
      (res) => {
        const items = Array.isArray(res) ? res : [];
        this.officers = items.map((item: ArbitrationOfficerItem) => ({
          id: item.id,
          name: item.name || "Unnamed Officer",
        }));
        this.lookupsLoading = false;
      },
      () => {
        this.officers = [];
        this.lookupsLoading = false;
      }
    );
  }

  applyFilters(): void {
    this.loadData();
  }

  clearFilters(): void {
    this.filters = {
      project: null,
      district: null,
      officer: null,
      search: "",
    };
    this.loadData();
  }

  openCreatePanel(): void {
    this.panelOpen = true;
    this.activityPanelOpen = false;
    this.editingCase = null;
    this.resetCaseForm();
    this.openDialog(this.caseDialogTemplate, "case");
  }

  openEditPanel(item: ArbitrationCaseItem): void {
    this.panelOpen = true;
    this.activityPanelOpen = false;
    this.editingCase = item;
    this.caseForm.reset({
      id: item?.id || null,
      project: item?.project || this.findOptionId(this.projects, item?.project_name),
      district: item?.district || this.findOptionId(this.districts, item?.district_name),
      officer: item?.officer || this.findOptionId(this.officers, item?.officer_name),
      total_cases: item?.total_cases ?? 0,
      cases_on_hearing: item?.cases_on_hearing ?? 0,
      disposed_cases: item?.disposed_cases ?? 0,
      comments: item?.comments || "",
      register_date: item?.register_date || this.getToday(),
    });
    this.openDialog(this.caseDialogTemplate, "case");
  }

  closePanel(): void {
    const activeDialog = this.dialogRef;
    this.panelOpen = false;
    this.editingCase = null;
    this.resetCaseForm();
    activeDialog?.close();
  }

  saveCase(): void {
    this.caseForm.markAllAsTouched();
    const payload = this.buildPayload();
    if (!payload) {
      this.toastr.error(this.getInvalidFieldsMessage());
      return;
    }

    this.saving = true;

    const request = this.editingCase?.id
      ? this.arbitrationService.updateCase(this.editingCase.id, payload)
      : this.arbitrationService.createCase(payload);

    request.subscribe(
      () => {
        this.saving = false;
        this.toastr.success(
          this.editingCase?.id ? "Arbitration case updated." : "Arbitration case created."
        );
        this.closePanel();
        this.loadData();
      },
      (err) => {
        this.saving = false;
        this.toastr.error(this.getApiErrorMessage(err, "Unable to save arbitration case."));
      }
    );
  }

  deleteCase(item: ArbitrationCaseItem): void {
    if (!item?.id) return;

    const shouldDelete = window.confirm(
      "Delete arbitration case " + (item.case_number || "#" + item.id) + "?"
    );
    if (!shouldDelete) return;

    this.deletingId = item.id;
    this.arbitrationService.deleteCase(item.id).subscribe(
      () => {
        this.deletingId = null;
        this.toastr.success("Arbitration case deleted.");
        if (this.selectedCase?.id === item.id) {
          this.closeActivityPanel();
        }
        this.loadData();
      },
      () => {
        this.deletingId = null;
        this.toastr.error("Unable to delete arbitration case.");
      }
    );
  }

  openActivityPanel(item: ArbitrationCaseItem): void {
    if (!item?.id) return;

    this.selectedCase = item;
    this.activityPanelOpen = true;
    this.panelOpen = false;
    this.activityForm = {
      activity_type: "comment",
      message: "",
    };
    this.openDialog(this.activityDialogTemplate, "activity");
    this.loadActivities(item.id);
  }

  closeActivityPanel(): void {
    const activeDialog = this.dialogRef;
    this.activityPanelOpen = false;
    this.selectedCase = null;
    this.selectedCaseActivities = [];
    this.activityForm = {
      activity_type: "comment",
      message: "",
    };
    activeDialog?.close();
  }

  saveActivity(): void {
    if (!this.selectedCase?.id || !this.activityForm.message.trim()) {
      this.toastr.error("Please enter an activity message.");
      return;
    }

    this.arbitrationService
      .createActivity({
        case: this.selectedCase.id,
        activity_type: this.activityForm.activity_type,
        message: this.activityForm.message.trim(),
      })
      .subscribe(
        () => {
          this.toastr.success("Activity added.");
          this.activityForm.message = "";
          this.loadActivities(this.selectedCase?.id);
        },
        () => {
          this.toastr.error("Unable to save activity.");
        }
      );
  }

  trackByCaseId(index: number, item: ArbitrationCaseItem): number | string {
    return item?.id || index;
  }

  @HostListener("document:keydown.escape")
  onEscapeKey(): void {
    if (this.panelOpen) {
      this.closePanel();
      return;
    }

    if (this.activityPanelOpen) {
      this.closeActivityPanel();
    }
  }

  getSummaryValue(value: number | undefined): string {
    return Number(value || 0).toLocaleString("en-IN");
  }

  getPendingCases(item: ArbitrationCaseItem): number {
    if (item?.pending_cases != null) return Number(item.pending_cases);
    return Number(item?.total_cases || 0) - Number(item?.disposed_cases || 0);
  }

  private buildPayload(): ArbitrationCasePayload | null {
    const rawFormValue = this.caseForm.getRawValue();
    const project = Number(rawFormValue.project);
    const district = Number(rawFormValue.district);
    const officer = Number(rawFormValue.officer);
    const totalCases = this.normalizeNumberValue(rawFormValue.total_cases);
    const casesOnHearing = this.normalizeNumberValue(rawFormValue.cases_on_hearing);
    const disposedCases = this.normalizeNumberValue(rawFormValue.disposed_cases);
    const registerDate = String(rawFormValue.register_date || "").trim();

    if (
      !Number.isFinite(project) ||
      !Number.isFinite(district) ||
      !Number.isFinite(officer) ||
      totalCases == null ||
      casesOnHearing == null ||
      disposedCases == null ||
      !registerDate
    ) {
      return null;
    }

    if (casesOnHearing > totalCases) {
      this.caseForm.get("cases_on_hearing")?.setErrors({ apiError: "Cases on hearing cannot be greater than total cases" });
      return null;
    }

    return {
      project,
      district,
      officer,
      total_cases: totalCases,
      cases_on_hearing: casesOnHearing,
      disposed_cases: disposedCases,
      comments: String(rawFormValue.comments || "").trim(),
      register_date: registerDate,
    };
  }

  private getFiltersQuery(): {
    project?: string;
    district?: string;
    officer?: string;
    search?: string;
  } {
    return {
      project: this.filters.project || undefined,
      district: this.filters.district || undefined,
      officer: this.filters.officer || undefined,
      search: (this.filters.search || "").trim() || undefined,
    };
  }

  private getToday(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private findOptionId(options: SelectOption[], name?: string): number | string | null {
    if (!name) return null;
    const option = options.find((item) => item.name === name);
    return option ? option.id : null;
  }

  private getInvalidFieldsMessage(): string {
    const missingFields: string[] = [];
    const rawFormValue = this.caseForm.getRawValue();
    const totalCases = this.normalizeNumberValue(rawFormValue.total_cases);
    const casesOnHearing = this.normalizeNumberValue(rawFormValue.cases_on_hearing);

    if (!Number.isFinite(Number(rawFormValue.project))) {
      missingFields.push("Project");
    }
    if (!Number.isFinite(Number(rawFormValue.district))) {
      missingFields.push("District");
    }
    if (!Number.isFinite(Number(rawFormValue.officer))) {
      missingFields.push("Officer");
    }
    if (this.normalizeNumberValue(rawFormValue.total_cases) == null) {
      missingFields.push("Total Cases");
    }
    if (this.normalizeNumberValue(rawFormValue.cases_on_hearing) == null) {
      missingFields.push("Cases on Hearing");
    }
    if (this.normalizeNumberValue(rawFormValue.disposed_cases) == null) {
      missingFields.push("Disposed Cases");
    }
    if (!String(rawFormValue.register_date || "").trim()) {
      missingFields.push("Register Date");
    }

    if (totalCases != null && casesOnHearing != null && casesOnHearing > totalCases) {
      return "Cases on hearing cannot be greater than total cases";
    }

    return missingFields.length
      ? "Please fill: " + missingFields.join(", ")
      : "Please fill all required arbitration case fields.";
  }

  private getApiErrorMessage(error: any, fallback: string): string {
    const errorBody = error?.error;

    if (!errorBody) {
      return fallback;
    }

    if (typeof errorBody === "string") {
      return errorBody;
    }

    if (Array.isArray(errorBody)) {
      return errorBody.join(" ");
    }

    const messages: string[] = [];
    Object.keys(errorBody).forEach((key) => {
      const value = errorBody[key];
      if (Array.isArray(value)) {
        messages.push(...value.map((message) => String(message)));
      } else if (value !== null && value !== undefined) {
        messages.push(String(value));
      }
    });

    return messages.length ? messages.join(" ") : fallback;
  }

  private normalizeNumberValue(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === "") return null;

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private openDialog(template: TemplateRef<any>, panelType: "case" | "activity"): void {
    this.dialogRef?.close();
    this.dialogRef = this.dialog.open(template, {
      width: "920px",
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - 32px)",
      autoFocus: false,
      restoreFocus: false,
      panelClass: "arbitration-dialog-panel",
      backdropClass: "arbitration-dialog-backdrop",
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
      if (panelType === "case") {
        this.panelOpen = false;
        this.editingCase = null;
        this.resetCaseForm();
        return;
      }

      this.activityPanelOpen = false;
      this.selectedCase = null;
      this.selectedCaseActivities = [];
      this.activityForm = {
        activity_type: "comment",
        message: "",
      };
    });
  }

  private resetCaseForm(): void {
    this.caseForm.reset({
      id: null,
      project: null,
      district: null,
      officer: null,
      total_cases: null,
      cases_on_hearing: null,
      disposed_cases: null,
      comments: "",
      register_date: this.getToday(),
    });
  }

  private loadActivities(caseId: number | string | undefined): void {
    if (!caseId) {
      this.selectedCaseActivities = [];
      return;
    }

    this.arbitrationService.listActivities(caseId).subscribe(
      (res) => {
        this.selectedCaseActivities = Array.isArray(res) ? res : [];
      },
      () => {
        this.selectedCaseActivities = [];
      }
    );
  }
}
